import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Shield, Upload, X, Plus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';

export default function AddService() {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const isEditMode = Boolean(serviceId);
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [idVerified, setIdVerified] = useState(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Custom category / subcategory suggestion
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [customCategoryName, setCustomCategoryName] = useState('');
  const [submittingCategory, setSubmittingCategory] = useState(false);

  const [showCustomSubcategory, setShowCustomSubcategory] = useState(false);
  const [customSubcategoryName, setCustomSubcategoryName] = useState('');
  const [submittingSubcategory, setSubmittingSubcategory] = useState(false);

  const [basicPrice, setBasicPrice] = useState('');
  const [basicDescription, setBasicDescription] = useState('');
  const [basicDelivery, setBasicDelivery] = useState('3');

  const [standardPrice, setStandardPrice] = useState('');
  const [standardDescription, setStandardDescription] = useState('');
  const [standardDelivery, setStandardDelivery] = useState('5');

  const [premiumPrice, setPremiumPrice] = useState('');
  const [premiumDescription, setPremiumDescription] = useState('');
  const [premiumDelivery, setPremiumDelivery] = useState('7');
  const [serviceImages, setServiceImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [deliveryType, setDeliveryType] = useState('physical');

  useEffect(() => {
    fetchCategories();
    checkIdStatus();
    if (isEditMode) loadService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  const loadService = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/services/${serviceId}`);
      if (!r.ok) throw new Error('Service not found');
      const s = await r.json();
      setTitle(s.title || '');
      setDescription(s.description || '');
      setSelectedCategory(s.category_id || null);
      setSelectedSubcategory(s.subcategory_id || null);
      setDeliveryType(s.delivery_type || 'physical');
      setExistingImages(s.images || []);
      const pkgs = s.packages || [];
      const byName = Object.fromEntries(pkgs.map((p) => [p.name, p]));
      if (byName.basic) {
        setBasicPrice(String(byName.basic.price ?? ''));
        setBasicDescription(byName.basic.description || '');
        setBasicDelivery(String(byName.basic.delivery_days ?? '3'));
      }
      if (byName.standard) {
        setStandardPrice(String(byName.standard.price ?? ''));
        setStandardDescription(byName.standard.description || '');
        setStandardDelivery(String(byName.standard.delivery_days ?? '5'));
      }
      if (byName.premium) {
        setPremiumPrice(String(byName.premium.price ?? ''));
        setPremiumDescription(byName.premium.description || '');
        setPremiumDelivery(String(byName.premium.delivery_days ?? '7'));
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load service');
      navigate('/dashboard/vendor/services');
    } finally {
      setLoading(false);
    }
  };

  const checkIdStatus = async () => {
    try {
      const res = await fetch(`${API}/vendors/me`, { headers: getAuthHeaders() });
      if (res.ok) {
        const vendor = await res.json();
        // Stripe Connect now handles identity verification (with grandfathered legacy uploads)
        setIdVerified(!!(vendor.stripe_connected || vendor.id_verified || vendor.id_document_url));
      }
    } catch {
      setIdVerified(false);
    }
  };

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/categories`);
      if (response.ok) {
        const raw = await response.json();
        // Sort categories A-Z, and each category's subs A-Z
        const sorted = [...raw]
          .map((c) => ({
            ...c,
            subcategories: [...(c.subcategories || [])].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            ),
          }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setCategories(sorted);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  };

  // Submit a brand-new custom category
  const submitCustomCategory = async () => {
    if (!customCategoryName.trim()) return;
    setSubmittingCategory(true);
    try {
      const r = await fetch(`${API}/categories/suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: customCategoryName.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Failed to add category');
      if (data.rejected) {
        toast.error(data.reason || 'That category is not allowed.');
        return;
      }
      // Add to list + select it (keep alphabetical)
      setCategories((prev) => {
        const exists = prev.some((c) => c.category_id === data.category.category_id);
        if (exists) return prev;
        return [...prev, data.category].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
      });
      setSelectedCategory(data.category.category_id);
      setSelectedSubcategory(null);
      setCustomCategoryName('');
      setShowCustomCategory(false);
      toast.success(data.message || 'Category added — now pick a subcategory or continue.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingCategory(false);
    }
  };

  // Submit a brand-new custom subcategory under the currently selected category
  const submitCustomSubcategory = async () => {
    if (!selectedCategory) {
      toast.error('Pick a category first.');
      return;
    }
    if (!customSubcategoryName.trim()) return;
    setSubmittingSubcategory(true);
    try {
      const r = await fetch(`${API}/categories/subcategory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ name: customSubcategoryName.trim(), category_id: selectedCategory }),
      });
      const sub = await r.json();
      if (!r.ok) throw new Error(sub?.detail || 'Failed to add subcategory');
      setCategories((prev) => prev.map((c) =>
        c.category_id === selectedCategory
          ? {
              ...c,
              subcategories: [...(c.subcategories || []), sub].sort((a, b) =>
                (a.name || '').localeCompare(b.name || '')
              ),
            }
          : c
      ));
      setSelectedSubcategory(sub.subcategory_id);
      setCustomSubcategoryName('');
      setShowCustomSubcategory(false);
      toast.success('Subcategory added.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmittingSubcategory(false);
    }
  };

  if (idVerified === false) {
    return (
      <div className="max-w-lg mx-auto text-center py-12" data-testid="id-verification-gate">
        <div className="w-16 h-16 rounded-2xl bg-[#8B5CF6]/20 flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-[#A78BFA]" />
        </div>
        <h2 className="text-xl font-bold text-white font-syne mb-2">Identity Verification Required</h2>
        <p className="text-[#9CA3AF] text-sm mb-6">
          To list services, verify your identity through Stripe — our payments partner. This unlocks payouts and protects buyers from fraud. We don&apos;t store your ID; Stripe handles it securely.
        </p>
        <Button onClick={() => navigate('/dashboard/vendor/profile')} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white">
          Verify Identity with Stripe
        </Button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCategory) { toast.error('Please select a category'); return; }
    if (!basicPrice || !standardPrice || !premiumPrice) { toast.error('Please set pricing for all 3 tiers'); return; }
    setSaving(true);
    try {
      const payload = {
        title,
        description,
        category_id: selectedCategory,
        subcategory_id: selectedSubcategory,
        delivery_type: deliveryType,
        packages: [
          { name: 'basic', title: 'Basic Package', price: parseFloat(basicPrice), description: basicDescription || 'Basic package', delivery_days: parseInt(basicDelivery), features: basicDescription ? basicDescription.split('\n').filter((f) => f.trim()) : ['Basic service'] },
          { name: 'standard', title: 'Standard Package', price: parseFloat(standardPrice), description: standardDescription || 'Standard package', delivery_days: parseInt(standardDelivery), features: standardDescription ? standardDescription.split('\n').filter((f) => f.trim()) : ['Standard service'] },
          { name: 'premium', title: 'Premium Package', price: parseFloat(premiumPrice), description: premiumDescription || 'Premium package', delivery_days: parseInt(premiumDelivery), features: premiumDescription ? premiumDescription.split('\n').filter((f) => f.trim()) : ['Premium service'] },
        ],
      };
      const url = isEditMode ? `${API}/services/${serviceId}` : `${API}/services/create`;
      const method = isEditMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        const targetId = isEditMode ? serviceId : data.service_id;
        for (const img of serviceImages) {
          const formData = new FormData();
          formData.append('file', img.file);
          try {
            await fetch(`${API}/services/${targetId}/upload-image`, { method: 'POST', headers: getAuthHeaders(), body: formData });
          } catch { /* non-blocking */ }
        }
        toast.success(isEditMode ? 'Service updated!' : 'Service created!');
        navigate('/dashboard/vendor/services');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Failed to ${isEditMode ? 'update' : 'create'} service`);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const selectedCategoryData = categories.find((c) => c.category_id === selectedCategory);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-white/5 rounded-2xl w-48 animate-pulse"></div>
        <div className="h-64 bg-white/5 rounded-2xl animate-pulse"></div>
      </div>
    );
  }

  const tierConfig = [
    { key: 'basic', name: 'Basic', num: '1', price: basicPrice, setPrice: setBasicPrice, desc: basicDescription, setDesc: setBasicDescription, delivery: basicDelivery, setDelivery: setBasicDelivery, border: 'border-white/10', bg: 'bg-white/5' },
    { key: 'standard', name: 'Standard', num: '2', price: standardPrice, setPrice: setStandardPrice, desc: standardDescription, setDesc: setStandardDescription, delivery: standardDelivery, setDelivery: setStandardDelivery, border: 'border-[#8B5CF6]/40', bg: 'bg-[#8B5CF6]/5', popular: true },
    { key: 'premium', name: 'Premium', num: '3', price: premiumPrice, setPrice: setPremiumPrice, desc: premiumDescription, setDesc: setPremiumDescription, delivery: premiumDelivery, setDelivery: setPremiumDelivery, border: 'border-[#EC4899]/30', bg: 'bg-[#EC4899]/5' },
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/dashboard/vendor/services')} className="p-2 rounded-xl text-[#9CA3AF] hover:text-white hover:bg-white/5 transition-colors">
          <ChevronRight size={20} className="rotate-180" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="add-service-title">
            {isEditMode ? 'Edit Service' : 'Add New Service'}
          </h1>
          <p className="text-sm text-[#9CA3AF] mt-1">
            {isEditMode ? 'Update your service details, pricing, and category' : 'Create a new service offering for your clients'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <GlassCard className="p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white font-syne">Service Details</h2>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Service Title *</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., Professional Logo Design" className="bg-white/5 border-white/10 text-white rounded-xl h-11 placeholder:text-white/20" required data-testid="service-title" />
          </div>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-1 block">Description *</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your service in detail..." className="bg-white/5 border-white/10 text-white rounded-xl min-h-[120px] placeholder:text-white/20" required data-testid="service-description" />
          </div>
        </GlassCard>

        <GlassCard className="p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white font-syne">Category & Subcategory</h2>
          <div>
            <Label className="text-[#9CA3AF] text-xs mb-3 block">Select Category *</Label>
            <div className="flex flex-wrap gap-2 max-h-64 overflow-y-auto p-3 bg-white/5 rounded-xl" data-testid="category-selection">
              {categories.map((cat) => (
                <button
                  key={cat.category_id}
                  type="button"
                  onClick={() => { setSelectedCategory(cat.category_id); setSelectedSubcategory(null); }}
                  className={`px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                    selectedCategory === cat.category_id
                      ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-semibold shadow-[0_0_15px_rgba(139,92,246,0.3)]'
                      : 'bg-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/20'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowCustomCategory(true)}
                className="px-4 py-2 rounded-full text-sm border border-dashed border-[#8B5CF6]/50 text-[#A78BFA] hover:text-white hover:bg-[#8B5CF6]/20 transition-all flex items-center gap-1"
                data-testid="add-custom-category-btn"
              >
                <Plus size={14} /> Add category
              </button>
            </div>
            {showCustomCategory && (
              <div className="mt-3 p-4 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 space-y-3" data-testid="custom-category-panel">
                <Label className="text-stone-300 text-sm">Describe your service category</Label>
                <Input
                  value={customCategoryName}
                  onChange={(e) => setCustomCategoryName(e.target.value)}
                  placeholder="e.g., Beadwork, Traditional Healing, Silversmithing"
                  className="bg-white/5 border-white/10 text-white rounded-xl h-11"
                  maxLength={60}
                  data-testid="custom-category-input"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={submitCustomCategory}
                    disabled={!customCategoryName.trim() || submittingCategory}
                    className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm"
                    data-testid="submit-custom-category"
                  >
                    {submittingCategory ? <><Loader2 size={14} className="animate-spin mr-1" /> Adding…</> : 'Add Category'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => { setShowCustomCategory(false); setCustomCategoryName(''); }}
                    className="text-stone-500 text-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
          {selectedCategory && (
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-3 block">Select Subcategory (Optional)</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-white/5 rounded-xl" data-testid="subcategory-selection">
                {(selectedCategoryData?.subcategories || []).map((sub) => (
                  <button
                    key={sub.subcategory_id}
                    type="button"
                    onClick={() => setSelectedSubcategory(sub.subcategory_id)}
                    className={`px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                      selectedSubcategory === sub.subcategory_id
                        ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white font-semibold shadow-[0_0_15px_rgba(139,92,246,0.3)]'
                        : 'bg-white/10 text-[#9CA3AF] hover:text-white hover:bg-white/20'
                    }`}
                  >
                    {sub.name}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setShowCustomSubcategory(true)}
                  className="px-4 py-2 rounded-full text-sm border border-dashed border-[#8B5CF6]/50 text-[#A78BFA] hover:text-white hover:bg-[#8B5CF6]/20 transition-all flex items-center gap-1"
                  data-testid="add-custom-subcategory-btn"
                >
                  <Plus size={14} /> Add subcategory
                </button>
              </div>
              {showCustomSubcategory && (
                <div className="mt-3 p-4 rounded-xl border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 space-y-3" data-testid="custom-subcategory-panel">
                  <Label className="text-stone-300 text-sm">New subcategory under {selectedCategoryData?.name}</Label>
                  <Input
                    value={customSubcategoryName}
                    onChange={(e) => setCustomSubcategoryName(e.target.value)}
                    placeholder="e.g., Turquoise inlay"
                    className="bg-white/5 border-white/10 text-white rounded-xl h-11"
                    maxLength={60}
                    data-testid="custom-subcategory-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={submitCustomSubcategory}
                      disabled={!customSubcategoryName.trim() || submittingSubcategory}
                      className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm"
                      data-testid="submit-custom-subcategory"
                    >
                      {submittingSubcategory ? <><Loader2 size={14} className="animate-spin mr-1" /> Adding…</> : 'Add Subcategory'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => { setShowCustomSubcategory(false); setCustomSubcategoryName(''); }}
                      className="text-stone-500 text-sm"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-6 space-y-4" data-testid="delivery-type-card">
          <h2 className="text-lg font-semibold text-white font-syne">Fulfillment Type</h2>
          <p className="text-[#9CA3AF] text-sm">How do you deliver this work? This controls the order progress tracker the customer sees.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: 'physical', title: 'Physical Product', sub: 'You ship something — tracking required on ship step' },
              { key: 'digital', title: 'Digital Delivery', sub: 'Files / downloads — no shipping or tracking' },
              { key: 'service', title: 'In-Person Service', sub: 'On-site work — en route / arrived milestones' },
            ].map((opt) => (
              <button
                type="button"
                key={opt.key}
                onClick={() => setDeliveryType(opt.key)}
                className={`text-left p-4 rounded-2xl border transition-all ${
                  deliveryType === opt.key
                    ? 'border-[#8B5CF6]/60 bg-[#8B5CF6]/10 shadow-[0_0_20px_rgba(139,92,246,0.2)]'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                }`}
                data-testid={`delivery-type-${opt.key}`}
              >
                <div className="text-white text-sm font-semibold mb-1">{opt.title}</div>
                <div className="text-[#9CA3AF] text-xs">{opt.sub}</div>
              </button>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white font-syne">Pricing Packages</h2>
          <p className="text-[#9CA3AF] text-sm">Set up 3 pricing tiers. Customers choose between Basic, Standard, or Premium.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tierConfig.map((tier) => (
              <div key={tier.key} className={`p-6 rounded-2xl border ${tier.border} ${tier.bg}`}>
                <h3 className="font-bold text-white mb-4 flex items-center gap-2 font-syne">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${tier.popular ? 'bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white' : 'bg-white/10 text-[#9CA3AF]'}`}>{tier.num}</span>
                  {tier.name}
                  {tier.popular && <span className="text-[10px] bg-[#8B5CF6]/20 text-[#A78BFA] px-2 py-0.5 rounded-full ml-auto">Popular</span>}
                </h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#9CA3AF] text-xs">Price ($) *</Label>
                    <Input type="number" min="1" value={tier.price} onChange={(e) => tier.setPrice(e.target.value)} placeholder={tier.key === 'basic' ? '50' : tier.key === 'standard' ? '100' : '200'} className="bg-white/5 border-white/10 text-white rounded-xl h-10 mt-1 placeholder:text-white/20" required data-testid={`${tier.key}-price`} />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] text-xs">What's Included</Label>
                    <Textarea value={tier.desc} onChange={(e) => tier.setDesc(e.target.value)} placeholder="e.g., 1 concept, 2 revisions" className="bg-white/5 border-white/10 text-white rounded-xl min-h-[80px] text-sm mt-1 placeholder:text-white/20" />
                  </div>
                  <div>
                    <Label className="text-[#9CA3AF] text-xs">Delivery (days)</Label>
                    <Input type="number" min="1" value={tier.delivery} onChange={(e) => tier.setDelivery(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl h-10 mt-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white font-syne">Work Photos</h2>
          <p className="text-[#9CA3AF] text-sm">Upload up to 5 photos showcasing your work for this service (like Fiverr portfolio).</p>
          {isEditMode && existingImages.length > 0 && (
            <div>
              <p className="text-[#64748B] text-xs mb-2">Existing photos (tap × to remove)</p>
              <div className="flex flex-wrap gap-3 mb-3">
                {existingImages.map((url, i) => (
                  <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 group">
                    <img
                      src={url.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url}
                      alt={`Existing ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const r = await fetch(`${API}/services/${serviceId}/image`, {
                            method: 'DELETE',
                            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                            body: JSON.stringify({ image_url: url }),
                          });
                          if (!r.ok) throw new Error('Failed');
                          setExistingImages((prev) => prev.filter((u) => u !== url));
                          toast.success('Photo removed');
                        } catch {
                          toast.error('Failed to remove photo');
                        }
                      }}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-existing-image-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-3">
            {serviceImages.map((img, i) => (
              <div key={i} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 group">
                <img src={img.preview} alt={`Work ${i + 1}`} className="w-full h-full object-cover" />
                <button type="button" onClick={() => setServiceImages((prev) => prev.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}>
                  <X size={12} />
                </button>
              </div>
            ))}
            {(existingImages.length + serviceImages.length) < 5 && (
              <label className="w-24 h-24 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#8B5CF6]/50 transition-colors" data-testid="add-service-image">
                <Upload size={20} className="text-[#9CA3AF] mb-1" />
                <span className="text-[10px] text-[#9CA3AF]">{existingImages.length + serviceImages.length}/5</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file && (existingImages.length + serviceImages.length) < 5) {
                    setServiceImages((prev) => [...prev, { file, preview: URL.createObjectURL(file) }]);
                  }
                  e.target.value = '';
                }} />
              </label>
            )}
          </div>
        </GlassCard>

        <div className="flex items-center gap-4">
          <button type="submit" disabled={saving} className="px-8 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] disabled:opacity-50 transition-all text-sm" data-testid="create-service-btn">
            {saving ? (isEditMode ? 'Saving…' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Service')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/vendor/services')} className="px-6 py-3 rounded-xl text-sm font-medium text-[#9CA3AF] border border-white/10 hover:text-white hover:bg-white/5 transition-all">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
